/**
 * Claiming a new server.
 *
 * This runs exactly once in the life of an installation: the API refuses every
 * other endpoint with `setup_required` until an owner exists, and answers 409
 * `owner_exists` here forever afterwards. That makes it the one form where the
 * cost of a mistake is highest and the person filling it in has the least
 * context, so it says plainly what the account is for and what happens next.
 *
 * The password rules are shown as they are met rather than enforced after the
 * fact. The server is still the authority — it also rejects a handful of
 * passwords everyone tries — but nobody should have to submit a form to find
 * out that twelve characters was the rule.
 */

import { useCallback, useId, useMemo, useState } from 'react'
import type { FormEvent, ReactElement, ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/api/auth'
import { api, isApiError } from '@/api/client'
import { queryKeys, useSetupStatus } from '@/api/queries'
import type { AuthSession, FieldErrors } from '@/api/types'
import { Button, TextField } from '@/components/ui'

import {
  AuthBrand,
  AuthCard,
  AuthFooter,
  AuthForm,
  AuthNotice,
  AuthShell,
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  Wordmark,
  describeRetry,
  firstFieldError,
  localTimezone,
  networkMessage,
  validateUsername,
} from './LoginPage'
import styles from './SetupPage.module.css'

/** Deliberately loose: the server owns the real rule, this only catches typos. */
function validateEmail(value: string): string | null {
  if (value === '') return 'Enter an email address.'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'That does not look like an email address.'
  if (value.length > 254) return 'That address is too long.'
  return null
}

// ── Live requirements ───────────────────────────────────────────────────────

interface RequirementProps {
  met: boolean
  children: ReactNode
}

/**
 * No `aria-live`: the list is bound to the password field with
 * `aria-describedby`, so it is read on demand rather than announced afresh on
 * every keystroke. The state travels in text, not only in colour.
 */
function Requirement({ met, children }: RequirementProps): ReactElement {
  return (
    <li className={met ? `${styles.requirement} ${styles.requirementMet}` : styles.requirement}>
      <span className={styles.requirementMark} aria-hidden="true">
        {met ? '✓' : '○'}
      </span>
      <span>
        {children}
        <span className="sr-only">{met ? ' — met' : ' — not met yet'}</span>
      </span>
    </li>
  )
}

// ── The page ────────────────────────────────────────────────────────────────

export function SetupPage(): ReactElement {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { refresh } = useAuth()
  const setupStatus = useSetupStatus()

  const [displayName, setDisplayName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  const [submitted, setSubmitted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [serverErrors, setServerErrors] = useState<FieldErrors>({})
  const [claimed, setClaimed] = useState(false)

  const requirementsId = useId()

  const usernameProblem = useMemo(() => validateUsername(username), [username])
  const emailProblem = useMemo(() => validateEmail(email), [email])

  const longEnough = password.length >= MIN_PASSWORD_LENGTH
  const tooLong = password.length > MAX_PASSWORD_LENGTH
  const matches = password !== '' && password === confirmation

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault()
      setSubmitted(true)
      setNotice(null)
      setServerErrors({})

      if (usernameProblem !== null || emailProblem !== null || !longEnough || tooLong || !matches) {
        setNotice('Check the highlighted fields and try again.')
        return
      }

      setBusy(true)
      try {
        // `password_confirmation` is sent as well as checked here, so the two
        // entries are compared once more by the side that stores the hash.
        await api.post<AuthSession>('/setup/admin', {
          username,
          email,
          password,
          password_confirmation: confirmation,
          display_name: displayName.trim() === '' ? undefined : displayName.trim(),
          timezone: localTimezone(),
        })

        // The response opened a session and set the cookies, so there is no
        // second sign-in to do — only the two cached facts that are now stale:
        // whether setup is required, and who is signed in.
        await queryClient.invalidateQueries({ queryKey: queryKeys.setupStatus() })
        await refresh()

        navigate('/onboarding', { replace: true })
      } catch (caught) {
        if (!isApiError(caught)) {
          setNotice(networkMessage())
        } else if (caught.code === 'owner_exists' || caught.code === 'conflict') {
          setClaimed(true)
        } else if (caught.code === 'validation_failed') {
          setServerErrors(caught.fieldErrors)
          setNotice('The server did not accept these details. See the messages below each field.')
        } else if (caught.code === 'rate_limited') {
          setNotice(`Too many attempts. Try again ${describeRetry(caught.retryAfterSeconds)}.`)
        } else {
          setNotice(caught.message)
        }
      } finally {
        setBusy(false)
      }
    },
    [
      confirmation,
      displayName,
      email,
      emailProblem,
      longEnough,
      matches,
      navigate,
      password,
      queryClient,
      refresh,
      tooLong,
      username,
      usernameProblem,
    ],
  )

  if (claimed) {
    return (
      <AuthShell>
        <AuthBrand />
        <AuthCard>
          <div className={styles.intro}>
            <h1 className={styles.title}>This server already has an owner</h1>
            <p className={styles.lede}>
              Setup runs once. Somebody has claimed this server already, so there is nothing left to
              do here — sign in with an account instead.
            </p>
          </div>
          <Link to="/login">Go to sign in</Link>
        </AuthCard>
      </AuthShell>
    )
  }

  const serverId = setupStatus.data?.server_id ?? null
  const productVersion = setupStatus.data?.product_version ?? null

  return (
    <AuthShell wide>
      <div className={styles.intro}>
        <Wordmark />
        <h1 className={styles.title}>Welcome to relaii</h1>
        <p className={styles.lede}>
          This server is new and nobody owns it yet. Create the first account to claim it.
        </p>
      </div>

      <AuthCard>
        <div className={styles.callout}>
          <p className={styles.calloutTitle}>What this account is</p>
          <p className={styles.calloutText}>
            The account you create here becomes the <strong>owner</strong> of this server: an
            administrator who can also transfer ownership and rotate the federation keys. This page
            works only once — as soon as the owner exists it stops accepting anything, so nobody who
            finds this address later can claim the server out from under you.
          </p>
          {serverId === null ? null : (
            <p className={styles.calloutText}>
              You are claiming <span className={styles.serverId}>{serverId}</span>
              {productVersion === null ? null : `, running relaii ${productVersion}`}.
            </p>
          )}
        </div>

        <AuthForm onSubmit={(event) => void onSubmit(event)}>
          {notice === null ? null : <AuthNotice tone="error">{notice}</AuthNotice>}

          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>About you</legend>

            <div className={styles.row}>
              <TextField
                label="Name"
                name="name"
                autoComplete="name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                hint="How your name appears in conversations."
                error={firstFieldError(serverErrors, 'display_name')}
              />

              <TextField
                label="Username"
                name="username"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                hint="Lowercase letters, digits, dot, dash or underscore."
                error={
                  firstFieldError(serverErrors, 'username') ??
                  (submitted || username !== '' ? usernameProblem : null)
                }
                required
              />
            </div>

            <TextField
              label="Email address"
              name="email"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              hint="Used to sign in and to reset your password."
              error={
                firstFieldError(serverErrors, 'email') ??
                (submitted || email !== '' ? emailProblem : null)
              }
              required
            />
          </fieldset>

          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Your password</legend>

            <TextField
              label="Password"
              name="new-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              aria-describedby={requirementsId}
              error={
                firstFieldError(serverErrors, 'password') ??
                (tooLong ? `That is longer than ${MAX_PASSWORD_LENGTH} characters.` : null)
              }
              required
            />

            <ul className={styles.requirements} id={requirementsId}>
              <Requirement met={longEnough}>At least {MIN_PASSWORD_LENGTH} characters</Requirement>
              <Requirement met={matches}>Both entries match</Requirement>
            </ul>

            <TextField
              label="Confirm password"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              error={
                firstFieldError(serverErrors, 'password_confirmation') ??
                (confirmation !== '' && !matches ? 'The two passwords do not match.' : null)
              }
              required
            />
          </fieldset>

          <Button type="submit" variant="primary" fullWidth loading={busy}>
            Create owner account
          </Button>
        </AuthForm>
      </AuthCard>

      <AuthFooter>
        A few words strung together make a stronger password than a short one with symbols in it.
        Nothing you type here leaves this server.
      </AuthFooter>
    </AuthShell>
  )
}
